import "./CMSUsers.css";

import React, { useCallback, useEffect, useRef, useState } from "react";

import CMSAddUser from "./CMSAddUser";
import { CMSService } from "../../../services/CMSService";
import { CMSUser } from "../../../types/CMSTypes";
import { SearchService } from "../../../services/searchService";
import { useLoader } from "../../../hooks/useLoader";
import { usePagination } from "../../../hooks/usePagination";

type Props = {
  allUsersData: { users: CMSUser[]; pagination: { hasMore: boolean } } | null;
  initialCalls: Function;
  isAdminUser: boolean;
  getAllUsersFromServer: Function;
  showOnlyTable?: boolean;
  customerId?: string;
};

const CMSUsers: React.FC<Props> = ({
  allUsersData,
  initialCalls,
  isAdminUser,
  getAllUsersFromServer,
  showOnlyTable = false,
  customerId,
}) => {
  const { showLoading } = useLoader();
  const [searchString, setSearchString] = useState("");
  const [searchResults, setSearchResults] = useState<CMSUser[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  usePagination(!!allUsersData?.pagination?.hasMore, getAllUsersFromServer);

  const performSearch = useCallback(async (searchTerm: string) => {
    try {
      setIsSearching(true);
      const response = await SearchService.searchUsersByName({
        name: searchTerm,
      });
      setSearchResults(response?.data?.users || []);
    } catch (error) {
      console.error("Search failed:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Effect to trigger search when searchString changes
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (searchString.trim()) {
      timeoutRef.current = setTimeout(() => {
        performSearch(searchString);
      }, 500);
    } else {
      setSearchResults(null);
      setIsSearching(false);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchString, performSearch]);

  const deleteUserFromPlan = async (user: CMSUser) => {
    try {
      setIsLoading(true);
      showLoading(true);
      const response = await CMSService.removeUserFromPlan({
        userId: user?.user_id,
      });
      setIsLoading(false);
      showLoading(false);
      alert("User removed successfully!");
      initialCalls();
    } catch (error) {
      setIsLoading(false);
      showLoading(false);
      if (error.response.data.message) {
        alert(error.response.data.message);
      } else {
        alert("Something went wrong");
      }
    }
  };

  const handleOnChangeTextOnSearch = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;
    setSearchString(value);
  };

  const handleDeleteUser = (user: CMSUser) => {
    const ok = window.confirm(
      `Remove ${user?.user_id} from this customer account?\n\nTheir login is kept and they stop receiving briefings. They can be re-attached later via Add User.`
    );
    if (ok) deleteUserFromPlan(user);
  };

  const handleDeleteAccount = async (user: CMSUser) => {
    const ok = window.confirm(
      `PERMANENTLY delete the account ${user?.user_id}?\n\nThis removes their login and subscription entirely and cannot be undone.`
    );
    if (!ok) return;
    try {
      setIsLoading(true);
      showLoading(true);
      await CMSService.deleteUserAccount({ email: user?.user_id });
      setIsLoading(false);
      showLoading(false);
      alert("Account deleted permanently.");
      initialCalls();
    } catch (error) {
      setIsLoading(false);
      showLoading(false);
      alert(error?.response?.data?.message || "Something went wrong");
    }
  };

  const handleAddUserClick = () => {
    setIsAddUserModalOpen(true);
  };

  const handleCloseAddUserModal = () => {
    setIsAddUserModalOpen(false);
  };

  const handleAddUserSuccess = () => {
    initialCalls();
    getAllUsersFromServer();
  };

  // Determine which data to display
  const displayUsers = searchString.trim()
    ? searchResults || []
    : allUsersData?.users || [];

  return (
    <div className={showOnlyTable ? "cmsUsers-main-popup" : "cmsUsers-main"}>
      {isLoading && (
        <div className="cms-users-loader-overlay">
          <img
            src="/assets/loader_white.gif"
            alt="Loading..."
            style={{ width: "60px", height: "60px" }}
          />
        </div>
      )}
      <div className="header-container">
        <div style={{ fontSize: 24, fontWeight: 700, color: "#161616" }}>
          Users
        </div>
        {isAdminUser && (
          <button className="add-user-button" onClick={handleAddUserClick}>
            Add User
          </button>
        )}
      </div>

      {isAdminUser && !showOnlyTable && (
        <div className="top-row-users">
          <span className="delivery-info">Delivery Information</span>
          <div className="header-right">
            <span className="search-container">
              <button className="search-button">
                <img
                  src="./assets/searchcms.svg"
                  alt="Search"
                  className="search-icon"
                />
                <input
                  type="text"
                  placeholder="Search users by name, email, or type..."
                  className="search-input"
                  style={{
                    backgroundColor: "white",
                    border: "none",
                    borderRadius: "none",
                    display: "flex",
                  }}
                  value={searchString}
                  onChange={handleOnChangeTextOnSearch}
                />
                {isSearching && (
                  <div
                    style={{
                      marginLeft: "8px",
                      fontSize: "12px",
                      color: "#666",
                    }}
                  >
                    Searching...
                  </div>
                )}
              </button>
            </span>
          </div>
        </div>
      )}
      <div>
        <table className="report-table-cmsuser">
          <thead>
            <tr className="report-table-heading">
              <th>
                <div style={{ display: "flex", alignItems: "center" }}>
                  User's Name
                  <img
                    src="./assets/arrowdown.svg"
                    alt="arrowDown"
                    style={{
                      marginLeft: 5,
                    }}
                  />
                </div>
              </th>
              <th>
                <div style={{ display: "flex", alignItems: "center" }}>
                  First Name
                  <img
                    src="./assets/arrowdown.svg"
                    alt="arrowDown"
                    style={{
                      marginLeft: 5,
                    }}
                  />
                </div>
              </th>
              <th>Last Name</th>
              <th>Email</th>
              <th>User Type</th>
              {isAdminUser && <th>Actions</th>}
            </tr>
          </thead>
          <tbody className="report-table-body">
            {displayUsers.length > 0 ? (
              displayUsers.map((row, index) => (
                <tr key={index}>
                  <td>{row.name}</td>
                  <td
                    style={{
                      color: "#667085",
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                  >
                    {row.name.split(" ")[0]}
                  </td>
                  <td
                    style={{
                      color: "#667085",
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                  >
                    {row.name.split(" ")[1]}
                  </td>
                  <td
                    style={{
                      color: "#667085",
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                  >
                    {row.user_id}
                  </td>
                  <td
                    style={{
                      color: "#667085",
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                  >
                    {row.userType?.toUpperCase()}
                  </td>

                  <td
                    style={{
                      color: "#667085",
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                  >
                    <div
                      className="actions-container"
                      style={{ display: "flex", flexDirection: "row" }}
                    >
                      {(isAdminUser || row.userType !== "customer") && (
                        <img
                          src="./assets/delete.svg"
                          alt="remove from customer"
                          title="Remove from customer (keeps their login)"
                          className="action-icon"
                          onClick={() => handleDeleteUser(row)}
                        />
                      )}
                      {isAdminUser && (
                        <span
                          title="Delete account permanently"
                          onClick={() => handleDeleteAccount(row)}
                          style={{
                            cursor: "pointer",
                            color: "#8B3A3A",
                            fontSize: 12,
                            fontWeight: 600,
                            marginLeft: 10,
                            alignSelf: "center",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Delete account
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={isAdminUser ? 6 : 5}
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#667085",
                  }}
                >
                  {searchString.trim()
                    ? isSearching
                      ? "Searching..."
                      : "No users found matching your search."
                    : "No users available."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isAddUserModalOpen && (
        <CMSAddUser
          onClose={handleCloseAddUserModal}
          onSave={handleAddUserSuccess}
          customerId={customerId}
        />
      )}
    </div>
  );
};

export default CMSUsers;
